import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Search, 
  Download, 
  RefreshCw, 
  Filter,
  User,
  Briefcase,
  FileText,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import './ConsultaAsignaciones.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultaAsignaciones = () => {
  const { user, isAdmin, isEditor } = useAuth();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [gestiones, setGestiones] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
  const [expandedUsers, setExpandedUsers] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [usuariosRes, gestionesRes, gruposRes, institucionesRes] = await Promise.all([
        axios.get(`${API}/catalogos/asesores-cnc`, { headers }),
        axios.get(`${API}/gestiones?limit=5000`, { headers }),
        axios.get(`${API}/catalogos/grupos`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogos/instituciones`, { headers }).catch(() => ({ data: [] }))
      ]);

      setUsuarios(usuariosRes.data || []);
      setGestiones(gestionesRes.data.gestiones || gestionesRes.data || []);
      setGrupos(gruposRes.data || []);
      setInstituciones(institucionesRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Determinar si el usuario puede ver todos los registros
  const puedeVerTodos = isAdmin() || isEditor();

  // Calcular datos por usuario
  const datosUsuarios = useMemo(() => {
    const resultado = [];
    
    const usuariosAMostrar = puedeVerTodos 
      ? usuarios 
      : usuarios.filter(u => u.nombre_completo === user?.full_name || u.email === user?.email);

    usuariosAMostrar.forEach(usuario => {
      const nombreUsuario = usuario.nombre_completo || usuario.email;
      
      // Filtrar gestiones del usuario (como asesor CNC o asesor Probidad)
      const gestionesUsuario = gestiones.filter(g => 
        g.nombre_asesor_cnc === nombreUsuario || 
        g.asesor_probidad_nombre === nombreUsuario
      );
      
      // Agrupar por institución
      const institucionesMap = {};
      gestionesUsuario.forEach(g => {
        const inst = g.institucion || 'Sin institución';
        if (!institucionesMap[inst]) {
          institucionesMap[inst] = {
            nombre: inst,
            total: 0,
            completadas: 0,
            enProceso: 0,
            comoAsesorCNC: 0,
            comoAsesorProbidad: 0
          };
        }
        institucionesMap[inst].total++;
        if (g.estado === 'Completada / Archivada') {
          institucionesMap[inst].completadas++;
        } else {
          institucionesMap[inst].enProceso++;
        }
        if (g.nombre_asesor_cnc === nombreUsuario) {
          institucionesMap[inst].comoAsesorCNC++;
        }
        if (g.asesor_probidad_nombre === nombreUsuario) {
          institucionesMap[inst].comoAsesorProbidad++;
        }
      });
      
      const institucionesArray = Object.values(institucionesMap);
      const totalCasos = gestionesUsuario.length;
      
      // Calcular porcentajes
      institucionesArray.forEach(inst => {
        inst.porcentaje = totalCasos > 0 ? ((inst.total / totalCasos) * 100).toFixed(1) : 0;
      });
      
      resultado.push({
        id: usuario.id || usuario.email,
        nombre: nombreUsuario,
        email: usuario.email,
        rol: usuario.rol || 'analista',
        grupo: usuario.grupo || '-',
        estado: usuario.activo !== false ? 'Activo' : 'Inactivo',
        instituciones: institucionesArray.sort((a, b) => b.total - a.total),
        totalCasos,
        completadas: gestionesUsuario.filter(g => g.estado === 'Completada / Archivada').length,
        enProceso: gestionesUsuario.filter(g => g.estado !== 'Completada / Archivada').length
      });
    });
    
    return resultado.sort((a, b) => b.totalCasos - a.totalCasos);
  }, [usuarios, gestiones, puedeVerTodos, user]);

  // Filtrar por usuario seleccionado
  const datosFiltrados = useMemo(() => {
    if (!usuarioSeleccionado) return datosUsuarios;
    return datosUsuarios.filter(u => u.nombre === usuarioSeleccionado);
  }, [datosUsuarios, usuarioSeleccionado]);

  // Toggle expandir/colapsar
  const toggleExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Expandir todos
  const expandirTodos = () => {
    const allExpanded = {};
    datosFiltrados.forEach(u => {
      allExpanded[u.id] = true;
    });
    setExpandedUsers(allExpanded);
  };

  // Colapsar todos
  const colapsarTodos = () => {
    setExpandedUsers({});
  };

  // Exportar a Excel
  const handleExportarExcel = () => {
    const datosExport = [];
    
    datosFiltrados.forEach(usuario => {
      usuario.instituciones.forEach(inst => {
        datosExport.push({
          'Usuario': usuario.nombre,
          'Email': usuario.email,
          'Rol': usuario.rol,
          'Grupo': usuario.grupo,
          'Estado': usuario.estado,
          'Institución': inst.nombre,
          'Casos Total': inst.total,
          'Completados': inst.completadas,
          'En Proceso': inst.enProceso,
          '% del Total': `${inst.porcentaje}%`
        });
      });
      
      // Agregar fila de totales por usuario
      datosExport.push({
        'Usuario': `TOTAL ${usuario.nombre}`,
        'Email': '',
        'Rol': '',
        'Grupo': '',
        'Estado': '',
        'Institución': 'TOTAL',
        'Casos Total': usuario.totalCasos,
        'Completados': usuario.completadas,
        'En Proceso': usuario.enProceso,
        '% del Total': '100%'
      });
    });
    
    const ws = XLSX.utils.json_to_sheet(datosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Asignaciones');
    XLSX.writeFile(wb, `Asignaciones_Usuarios_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  // Obtener color del rol
  const getRolColor = (rol) => {
    switch (rol?.toLowerCase()) {
      case 'administrador': return '#9C27B0';
      case 'editor': return '#2196F3';
      case 'analista': return '#4CAF50';
      case 'viewer': return '#607D8B';
      default: return '#666';
    }
  };

  if (loading) {
    return (
      <div className="consulta-loading">
        <div className="loader"></div>
        <p>Cargando asignaciones...</p>
      </div>
    );
  }

  return (
    <div className="consulta-asignaciones-container" data-testid="consulta-asignaciones">
      <div className="consulta-header">
        <div>
          <h1>
            <Building2 size={28} />
            Instituciones y Asignaciones
          </h1>
          <p>Consulta de instituciones y grupos asignados por usuario (Solo lectura)</p>
        </div>
        <div className="consulta-actions">
          <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Actualizar
          </button>
          <button className="btn btn-primary" onClick={handleExportarExcel}>
            <Download size={16} />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Filtro por Usuario (Solo Admin/Editor) */}
      {puedeVerTodos && (
        <div className="filtro-usuario-panel">
          <div className="filtro-usuario">
            <label>
              <User size={16} />
              Filtrar por Usuario:
            </label>
            <select
              value={usuarioSeleccionado}
              onChange={(e) => setUsuarioSeleccionado(e.target.value)}
            >
              <option value="">Todos los usuarios</option>
              {datosUsuarios.map(u => (
                <option key={u.id} value={u.nombre}>
                  {u.nombre} ({u.totalCasos} casos)
                </option>
              ))}
            </select>
          </div>
          <div className="expand-controls">
            <button className="btn btn-sm" onClick={expandirTodos}>
              <ChevronDown size={14} /> Expandir Todo
            </button>
            <button className="btn btn-sm" onClick={colapsarTodos}>
              <ChevronUp size={14} /> Colapsar Todo
            </button>
          </div>
        </div>
      )}

      {/* Resumen General */}
      <div className="resumen-general">
        <div className="resumen-card">
          <Users size={24} />
          <div>
            <span className="resumen-numero">{datosFiltrados.length}</span>
            <span className="resumen-texto">Usuarios</span>
          </div>
        </div>
        <div className="resumen-card">
          <FileText size={24} />
          <div>
            <span className="resumen-numero">
              {datosFiltrados.reduce((acc, u) => acc + u.totalCasos, 0)}
            </span>
            <span className="resumen-texto">Total Casos</span>
          </div>
        </div>
        <div className="resumen-card completadas">
          <TrendingUp size={24} />
          <div>
            <span className="resumen-numero">
              {datosFiltrados.reduce((acc, u) => acc + u.completadas, 0)}
            </span>
            <span className="resumen-texto">Completados</span>
          </div>
        </div>
        <div className="resumen-card en-proceso">
          <Briefcase size={24} />
          <div>
            <span className="resumen-numero">
              {datosFiltrados.reduce((acc, u) => acc + u.enProceso, 0)}
            </span>
            <span className="resumen-texto">En Proceso</span>
          </div>
        </div>
      </div>

      {/* Lista de Usuarios con Instituciones */}
      <div className="usuarios-lista">
        {datosFiltrados.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>No se encontraron usuarios con los filtros aplicados</p>
          </div>
        ) : (
          datosFiltrados.map(usuario => (
            <div key={usuario.id} className="usuario-card">
              <div 
                className="usuario-header"
                onClick={() => toggleExpand(usuario.id)}
              >
                <div className="usuario-info">
                  <div className="usuario-avatar">
                    {usuario.nombre.split(' ').map(n => n[0]).slice(0, 2).join('')}
                  </div>
                  <div>
                    <h3>{usuario.nombre}</h3>
                    <p>{usuario.email}</p>
                  </div>
                </div>
                <div className="usuario-meta">
                  <span 
                    className="rol-badge"
                    style={{ backgroundColor: getRolColor(usuario.rol) }}
                  >
                    {usuario.rol}
                  </span>
                  <span className="grupo-badge">
                    <Building2 size={12} />
                    {usuario.grupo}
                  </span>
                  <span className={`estado-badge ${usuario.estado.toLowerCase()}`}>
                    {usuario.estado}
                  </span>
                </div>
                <div className="usuario-stats">
                  <div className="stat">
                    <span className="stat-numero">{usuario.totalCasos}</span>
                    <span className="stat-label">Total</span>
                  </div>
                  <div className="stat completada">
                    <span className="stat-numero">{usuario.completadas}</span>
                    <span className="stat-label">Compl.</span>
                  </div>
                  <div className="stat proceso">
                    <span className="stat-numero">{usuario.enProceso}</span>
                    <span className="stat-label">Proceso</span>
                  </div>
                </div>
                <button className="btn-expand">
                  {expandedUsers[usuario.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
              
              {expandedUsers[usuario.id] && (
                <div className="usuario-detalle">
                  <h4>
                    <Building2 size={16} />
                    Instituciones Asignadas ({usuario.instituciones.length})
                  </h4>
                  
                  {usuario.instituciones.length === 0 ? (
                    <p className="sin-instituciones">Sin instituciones asignadas</p>
                  ) : (
                    <table className="instituciones-tabla">
                      <thead>
                        <tr>
                          <th>Institución</th>
                          <th>Total Casos</th>
                          <th>Completados</th>
                          <th>En Proceso</th>
                          <th>% del Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usuario.instituciones.map((inst, idx) => (
                          <tr key={idx}>
                            <td className="inst-nombre">{inst.nombre}</td>
                            <td className="numero">{inst.total}</td>
                            <td className="numero completada">{inst.completadas}</td>
                            <td className="numero proceso">{inst.enProceso}</td>
                            <td className="porcentaje">
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill"
                                  style={{ width: `${inst.porcentaje}%` }}
                                ></div>
                                <span>{inst.porcentaje}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        <tr className="fila-total">
                          <td><strong>TOTAL</strong></td>
                          <td className="numero"><strong>{usuario.totalCasos}</strong></td>
                          <td className="numero completada"><strong>{usuario.completadas}</strong></td>
                          <td className="numero proceso"><strong>{usuario.enProceso}</strong></td>
                          <td className="porcentaje"><strong>100%</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Nota de Solo Lectura */}
      <div className="nota-lectura">
        <Eye size={16} />
        <span>Esta vista es de solo lectura. Para modificar asignaciones, use el módulo de Administración.</span>
      </div>
    </div>
  );
};

export default ConsultaAsignaciones;
